import type { ComponentProps } from 'react';
import styled from '@emotion/styled';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import { tokens } from '@hotjar/hotsauce';
import { capitalize } from '@hotjar/insights-common/lib/string';

type BorderRadius = 'rounded' | 'circle';

interface ContainerProps {
  size: number;
  noBackground: boolean;
  format: BorderRadius;
  outline: boolean;
}

interface AvatarProps {
  className?: string;
  hideTooltip?: boolean;
  tooltipContent?: ComponentProps<typeof Tooltip>['content'];
  tooltipMaxWidth?: ComponentProps<typeof Tooltip>['maxWidth'];
  name?: string;
  url?: string;
  userRole?: string;
  // FIXME
  // eslint-disable-next-line react/no-unused-prop-types
  showInitials?: boolean;
  format?: BorderRadius;
  size?: number;
  outline?: boolean;
}

type InitialsLength = 1 | 2 | 3;

const FONT_SIZE_MULTIPLIERS = [0.5, 0.45, 0.32] as const;

const BORDER_RADIUS_MAP: Record<BorderRadius, string> = {
  rounded: tokens.radius.m,
  circle: '50%',
} as const;

const getSize = ({ size }: ContainerProps) => `${size}px`;
const getBackgroundColor = ({ noBackground }: ContainerProps) =>
  noBackground ? 'transparent' : tokens.color.neutral_600;
const getBorderRadius = ({ format }: ContainerProps) => BORDER_RADIUS_MAP[format];
const getShadow = ({ outline }: ContainerProps) =>
  outline ? `0 0 0 1px ${tokens.color.ui_background}` : 'none';
const getFontSize = ({ length, size }: { size: number; length: InitialsLength }) =>
  `${size * FONT_SIZE_MULTIPLIERS[length - 1]}px`;

const Container = styled('div', { shouldForwardProp: (prop: string) => prop === 'children' })`
  width: ${getSize};
  height: ${getSize};
  background-color: ${getBackgroundColor};
  border-radius: ${getBorderRadius};
  font-size: 100%;
  box-shadow: ${getShadow};

  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const Initials = styled('span', {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'length',
})`
  font-size: ${getFontSize};
  line-height: 1;
  color: ${tokens.color.text_default_inverse};
  font-family: Arial, sans-serif;
  font-weight: bold;
`;

const Image = styled.img`
  width: 100%;
`;

function getUserText(name: string, userRole: string | undefined): string {
  if (userRole) {
    return `${name} (${capitalize(userRole)})`;
  }

  return name;
}

/**
 * @deprecated use hotsauce
 * [`<Avatar />`](https://storybook.hotjar.com/?path=/docs/components-avatar--playground) instead
 * @see Avatar
 */

export const Avatar = ({
  className,
  format = 'circle',
  hideTooltip = false,
  tooltipContent,
  tooltipMaxWidth,
  name = '',
  size = 40,
  url,
  userRole,
  outline = false,
}: AvatarProps): JSX.Element => {
  const initials = name
    .toUpperCase()
    .split(' ')
    .slice(0, 3)
    .map((word) => word.charAt(0))
    .join('');

  // If there isn't a picture url at all or the users image is the default image we show initials instead.
  const showInitials = !url || url === '/static/app/img/default-avatar.png';

  const userText = getUserText(name, userRole);
  const tooltipText = tooltipContent ?? userText;

  return (
    <Tooltip content={tooltipText} enabled={!hideTooltip} maxWidth={tooltipMaxWidth}>
      <Container
        format={format}
        size={size}
        noBackground={!showInitials}
        outline={outline}
        className={className}
      >
        {!showInitials && <Image data-hj-suppress src={url} alt={`Avatar for ${name}`} />}
        {showInitials && (
          <Initials
            data-hj-suppress
            size={size}
            length={initials.length as InitialsLength}
            aria-label={userText}
          >
            {initials}
          </Initials>
        )}
      </Container>
    </Tooltip>
  );
};
